import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import { FadeUp } from "../components/motion";
import { fetchCategories, fetchProducts } from "../lib/api";

export default function ProductCategory() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchCategories().then((cats) => {
      const c = cats.find((x) => x.slug === slug);
      if (!c) return navigate("/products");
      setCategory(c);
    });
    fetchProducts({ category: slug }).then(setProducts).catch(() => {});
  }, [slug, navigate]);

  if (!category) {
    return (
      <PageWrapper>
        <div className="pt-[170px] pb-32 max-w-[1440px] mx-auto px-5 md:px-10">
          <div className="font-display text-3xl text-[#F2EDE3]/40">Loading collection…</div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      {/* Hero */}
      <section className="relative pt-[76px]" data-testid="category-hero">
        <div className="relative h-[380px] md:h-[480px] overflow-hidden">
          <img src={category.hero_image} alt={category.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/50 to-[#0A0908]/25" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 pb-10 w-full">
              <Link to="/products" data-testid="back-to-collection" className="eyebrow text-[#F2EDE3]/60 hover:text-[#D9A441] inline-flex items-center gap-2 mb-5 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> All Collections
              </Link>
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <div className="font-condensed text-[#D9A441] text-xl mb-2">{category.num}</div>
                  <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EDE3]" data-testid="category-title">
                    {category.name}
                  </h1>
                  <p className="mt-4 text-sm md:text-base text-[#F2EDE3]/60 max-w-xl leading-relaxed">{category.summary}</p>
                </div>
                <div className="panel px-6 py-4 text-center">
                  <div className="font-condensed text-3xl text-[#D9A441]">{products.length}</div>
                  <div className="eyebrow !text-[9px] text-[#F2EDE3]/60 mt-1">Models</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-16 lg:py-20" data-testid="category-products">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p, i) => (
              <FadeUp key={p.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/products/${p.slug}`}
                  data-testid={`category-product-${p.slug}`}
                  className="group block panel overflow-hidden hover:border-[#D9A441]/50 transition-colors duration-500"
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img src={p.image} alt={p.name} loading="lazy" className="product-card-img absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/70 to-transparent" />
                    <span className="absolute bottom-4 right-4 eyebrow !text-[9px] bg-[#D9A441] text-[#0A0908] px-3 py-1.5 rounded-sm">
                      {p.size_range}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-display text-xl text-[#F2EDE3] group-hover:text-[#D9A441] transition-colors">{p.name}</div>
                        <div className="text-xs text-[#F2EDE3]/50 mt-1.5">{p.form} · {p.deployment}</div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-[#D9A441] shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#D9A441]/12 flex items-center justify-between text-xs">
                      <span className="text-[#F2EDE3]/45">{p.use_cases[0]}</span>
                      <span className="text-[#D9A441] font-semibold">{p.price_range}</span>
                    </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>

          <FadeUp className="mt-16 panel px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="eyebrow text-[#D9A441] mb-2">Not sure which model fits?</div>
              <div className="font-display text-2xl md:text-3xl text-[#F2EDE3]">Talk to a NUVUN specialist.</div>
            </div>
            <Link to="/contact" data-testid="category-enquire-cta" className="btn-gold whitespace-nowrap">
              Start Your Project <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
}
